import { useCallback } from 'react';

import Konva from 'konva';
import { KonvaEventObject } from 'konva/lib/Node';
import { useShallow } from 'zustand/react/shallow';

import { useMagicPenStore } from '@src/stores';

import { getObjectIdFromNode } from '../../helpers';

import { ICanvasObject } from '@src/types';

const useObjectInteraction = ({
  stageRef,
  currentCreatedById,
  canAnnotate,
  emitUpdateAnnotation,
}: {
  stageRef: React.RefObject<Konva.Stage>;
  currentCreatedById: string;
  canAnnotate: boolean;
  emitUpdateAnnotation: (objects: ICanvasObject[]) => Promise<void>;
}) => {
  const { activeTool, setHoveredId, setSelectedId, bringToFront, objects } = useMagicPenStore(
    useShallow((state) => ({
      activeTool: state.activeTool,
      setHoveredId: state.setHoveredId,
      setSelectedId: state.setSelectedId,
      bringToFront: state.bringToFront,
      objects: state.objects,
    }))
  );

  const handleObjectClick = useCallback(
    (e: KonvaEventObject<MouseEvent>, handleEraserClick?: (node: Konva.Node) => void) => {
      // Disable all interactions if user can't annotate
      if (!canAnnotate) return;
      const node = e.target;

      const objectId = node ? getObjectIdFromNode(node) : '';
      const targetObject = objects.find((obj) => obj.id === objectId);
      const isCreatedByMe = targetObject?.createdBy?.peer_id === currentCreatedById;
      const canDeleteThis = targetObject?.createdBy?.peer_id === currentCreatedById || canAnnotate;
      // Handle eraser mode - click to delete (creator can only delete their own objects)
      if (activeTool === 'eraser' && handleEraserClick) {
        if (canDeleteThis) {
          handleEraserClick(node);
        }
        return;
      }

      // Handle select mode - only work in select mode
      if (activeTool !== 'select') return;

      const clickedObject = e.target;

      // Find the parent Group that has the actual object ID
      // The clicked object might be a Text or Rect inside a Group
      let objectWithId: Konva.Node | null = clickedObject;
      while (objectWithId && !objectWithId.id()) {
        //Grabbing the group, not the Text, for sticky note
        objectWithId = objectWithId.getParent();
      }

      if (objectId && objectId !== '' && isCreatedByMe) {
        // Bring selected object to front with update callback
        bringToFront(objectId, (updatedObjects) => {
          // Emit update to other peers
          emitUpdateAnnotation(updatedObjects);
        });
        setSelectedId(objectId);
      } else {
        setSelectedId(null);
      }
    },
    [canAnnotate, objects, currentCreatedById, activeTool, bringToFront, setSelectedId, emitUpdateAnnotation]
  );

  const handleObjectHover = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>, objectId: string) => {
      // Allow hover for tooltips even when can't annotate, but only in select/none/eraser/sticky modes
      if (!['select', 'eraser', 'sticky', 'none'].includes(activeTool)) return;

      if (objectId && objectId !== '') {
        setHoveredId(objectId);
        // Change cursor to pointer only in select mode and when user can annotate (eraser has custom cursor)
        const container = e.target.getStage()?.container();
        if (container && activeTool === 'select' && canAnnotate) {
          container.style.cursor = 'pointer';
        }
      }
    },
    [activeTool, setHoveredId, canAnnotate]
  );

  const handleObjectUnhover = useCallback(() => {
    setHoveredId(null);
    // Reset cursor based on active tool
    if (stageRef.current) {
      const container = stageRef.current.container();
      if (container) {
        // Don't override cursor in non-select modes (like eraser)
        if (activeTool === 'select') {
          container.style.cursor = 'default';
        }
      }
    }
  }, [activeTool, setHoveredId, stageRef]);

  // This function is meant to be called with additional parameters from the wrapper
  const handleStageClickInternal = useCallback(
    (
      e: Konva.KonvaEventObject<MouseEvent>,
      isEditingText: boolean,
    ) => {
      // Disable all interactions if user can't annotate
      if (!canAnnotate) return;
      // Don't deselect when editing text
      if (isEditingText) return;

      // Only handle stage clicks in select mode
      if (activeTool !== 'select') return;

      const clickedOnEmpty = e.target.getType() === 'Stage' || e.target.getType() === 'Layer';

      if (clickedOnEmpty) {
        // Clicked on empty space - deselect any selected object
        setSelectedId(null);
      }
    },
    [activeTool, setSelectedId, canAnnotate]
  );

  return {
    handleObjectClick,
    handleObjectHover,
    handleObjectUnhover,
    handleStageClick: handleStageClickInternal,
  };
};

export default useObjectInteraction;
